import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Tag } from '@carbon/react';
import { definePage, defineResource, ListQuery } from '@restavo/crud';
import EmptyState from 'src/components/Base/EmptyState';
import ImagesViewer from 'src/components/Base/ImagesViewer';
import MenuItemVariantForm from 'src/components/Menu/MenuItemVariantForm';
import { useBranches } from 'src/hooks/useRestaurantBranches';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import {
  tenantMenuItemVariantCreate,
  tenantMenuItemVariantDelete,
  tenantMenuItemVariants,
  tenantMenuItemVariantUpdate,
  tenantMenuItems,
  orgRestaurantBranches,
} from 'src/services/apis/tenantRequests';
import { RestaurantScope } from 'src/services/apis/tenantRequests';
import { MenuItemVariantEntity } from 'src/services/apis/types';
import { getImageUrl } from 'src/utils/images';
import { QUERIES } from 'src/utils/react-query';

/** The branch chip's compiled form, e.g. `specificForBranchIds||$in||a,b`. */
const BRANCH_FIELD = 'specificForBranchIds';

/**
 * Pulls the branch chip out of the compiled query and returns the ids it selects. The chip
 * has to be intercepted rather than forwarded: specificForBranchIds is a Postgres text
 * array and the CRUD backend has no membership predicate for it, so sending the filter
 * would 400. The narrowing happens in memory instead, over the fully-fetched list.
 */
function takeBranchFilter(filter?: string[]): { rest: string[]; ids: string[]; exclude: boolean } {
  const rest: string[] = [];
  let ids: string[] = [];
  let exclude = false;
  for (const entry of filter ?? []) {
    if (!entry.startsWith(`${BRANCH_FIELD}||`)) {
      rest.push(entry);
      continue;
    }
    const [, op, raw] = entry.split('||');
    exclude = op === '$ne' || op === '$notin';
    ids = (raw ?? '')
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return { rest, ids, exclude };
}

/**
 * The MenuItemVariants page config, as a hook because its columns and image viewer read
 * portal context. The branch filter is an ordinary chip in the Filters menu — see
 * `takeBranchFilter` for why the list wrapper applies it in memory. As with the control it
 * replaces, `total` stays the server's UNfiltered count while the rows are narrowed.
 */
export const useMenuItemVariantsPage = () => {
  const { t } = useTranslation();
  const currentRestaurant = useCurrentRestaurant();
  const { data: branches } = useBranches();

  const menuItemVariants = useMemo(
    () =>
      defineResource({
        key: QUERIES.MENU_ITEM_VARIANTS,
        // The hand-written hook sent no limit, so the server (alwaysPaginate, no configured
        // take) returned every row in one shot; 1000 is the practical match — the same
        // reach the pilot used for an effectively-unbounded list.
        pageSize: 1000,
        api: {
          list: async (scope: RestaurantScope, query?: ListQuery) => {
            const { rest, ids, exclude } = takeBranchFilter(query?.filter);
            const result = await tenantMenuItemVariants(scope, { ...query, filter: rest });
            if (ids.length === 0) {
              return result;
            }
            return {
              ...result,
              data: result.data.filter((item) => {
                const hit = ids.some((id) => item.specificForBranchIds.includes(id));
                return exclude ? !hit : hit;
              }),
            };
          },
          create: tenantMenuItemVariantCreate,
          update: tenantMenuItemVariantUpdate,
          remove: tenantMenuItemVariantDelete,
        },
        // The hand-written page filtered name/sku in memory over the fetched set.
        search: 'client',
        searchFields: ['name', 'sku'],
        // The menu-item filter had an equivalent control on the hand-written page and is a
        // plain server column, so it graduates to a chip; the menuItemId column shares this
        // module-scope load, so their options fetch once between them (spec §7.5).
        filterable: [
          {
            field: 'menuItemId',
            labelKey: 'common.menuItem',
            type: 'reference',
            load: tenantMenuItems,
            optionLabel: (item: any) => item.name,
          },
          // Branch was a ComboBox in the toolbar with its own `branch` URL param. As a chip
          // it shares the Filters menu with every other filter, carries its state in the
          // crud layer's own URL keys, and refetches through the query key like the rest —
          // which is what retired the manual refetch plumbing this hook used to need.
          {
            field: BRANCH_FIELD,
            labelKey: 'common.branch',
            type: 'reference',
            load: orgRestaurantBranches,
            optionLabel: (item: any) => item.name,
          },
          // A plain server column; takeBranchFilter passes every non-branch filter through.
          { field: 'available', labelKey: 'common.available', type: 'boolean' },
        ],
      }),
    []
  );

  const [viewingMenuItemVariantPhotos, setViewingMenuItemVariantPhotos] = useState<MenuItemVariantEntity[]>([]);

  const page = {
    ...definePage(menuItemVariants, {
      titleKey: 'titles.menuItemVariantsPage',
      descriptionKey: 'common.variants',
      createLabelKey: 'actions.createNewVariant',
      updateLabelKey: 'actions.updateVariant',
      deleteHeadingKey: 'messages.areYouSureToDeleteThisVariant',
      columns: [
        // The column a user edits a variant by must survive every prefs reconciliation.
        { key: 'name', headerKey: 'common.name', sortable: true, alwaysVisible: true },
        { key: 'sku', headerKey: 'common.sku' },
        {
          key: 'menuItemId',
          headerKey: 'common.menu',
          display: {
            kind: 'reference',
            load: tenantMenuItems,
            optionLabel: (item: any) => item.name,
          },
        },
        // currency renders via the portal's useMoney formatter — the same one the hand-written
        // cell used, minus the empty-string gap it left whenever price was missing.
        { key: 'price', headerKey: 'common.price', display: { kind: 'currency' } },
        {
          key: 'photos',
          headerKey: 'common.photo',
          render: (row: MenuItemVariantEntity) =>
            row.photos?.length ? (
              <Link onClick={() => setViewingMenuItemVariantPhotos([row])}>{row.photos[0]}</Link>
            ) : (
              t('messages.noPhotos')
            ),
          // The cell is a Link, which cannot be flattened; the photo id is what a
          // spreadsheet can actually use.
          toCsv: (row: MenuItemVariantEntity) => row.photos?.[0] ?? '',
        },
        {
          key: 'specificForBranchIds',
          headerKey: 'common.branches',
          render: (row: MenuItemVariantEntity) =>
            branches?.data
              ?.filter((branch) => row.specificForBranchIds.includes(branch.id))
              .map((branch) => (
                <Tag key={branch.id} type={'green'}>
                  {branch.name}
                </Tag>
              )),
          // Without this the export carries the raw branch UUIDs the Tags hide.
          toCsv: (row: MenuItemVariantEntity) =>
            (branches?.data ?? [])
              .filter((branch) => row.specificForBranchIds.includes(branch.id))
              .map((branch) => branch.name)
              .join('; '),
        },
        // The hand-written cell was a dead per-row Toggle (no onToggle handler, so it never
        // changed anything) — the same yes/no copy as static text, like the pilot's menus page.
        { key: 'available', headerKey: 'common.available', display: { kind: 'boolean' } },
      ],
      form: MenuItemVariantForm,
      emptyState: <EmptyState />,
      toPayload: (values: any, params) => {
        // The hand-written handler returned early here: nothing was submitted and the modal
        // stayed open. Throwing keeps both of those properties — the modal only closes when
        // the mutations behind it resolve — so the guard survives the seam verbatim.
        if (values.specificForBranchIds.length === 0 || !values.menuItemId) {
          throw new Error(t('messages.variantNeedsMenuItemAndBranch'));
        }
        return {
          ...values,
          restaurantId: params.restaurantId,
          tags: values.tags?.split(',').map((tag: string) => tag.trim()) || [],
          photos: values.photo ? [values.photo] : [],
          price: Number(values.price),
          order: parseInt(values.order, 10),
          blurhashPhotos: values.photoBlurhash ? [values.photoBlurhash] : [],
        };
      },
      useTitleVars: () => ({ restaurantName: currentRestaurant?.name }),
    }),
  };

  const imagesViewer = (
    <ImagesViewer
      images={viewingMenuItemVariantPhotos.map((item) => ({
        src: getImageUrl(item.photos[0])!,
        caption: `${currentRestaurant?.name} - ${item.name} ${t('common.photos')}`,
      }))}
      onClose={() => setViewingMenuItemVariantPhotos([])}
    />
  );

  return { page, imagesViewer };
};
